/* Native Liquid is the member authority. Public program data supplies rates only.
 * No member storage or completed-response cache. One request per render cycle.
 */
(() => {
  'use strict';
  const contextSelector = '[data-loyalty-context]';
  const stores = new Set(['66588d51-288c-4a56-9f04-58d761ad6d00', 'c27eca27-53a5-4efc-bd2d-9bf524c5cc94']);
  const timeout = 5000;
  let current = null;
  let generation = 0;
  let suspended = false;
  let pending = null;

  const all = selector => document.querySelectorAll(selector);
  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const text = value => typeof value === 'string' && value.length > 0;
  const state = marker => marker?.dataset;

  function restrict(marker) {
    // Native non-retail context can revoke eligibility before revalidation.
    // This only hides; retail restoration stays in the verified lifecycle.
    if (state(marker)?.loyaltyRetail === 'false') {
      all('[data-loyalty-root], [data-loyalty-slot]').forEach(el => { el.hidden = true; });
    }
  }

  function clear() {
    all('[data-loyalty-member]').forEach(el => {
      el.hidden = true;
      el.removeAttribute('aria-label');
      el.querySelector('[data-loyalty-name-text]').textContent = '';
    });
    all('[data-loyalty-rate], [data-loyalty-public-rate]').forEach(el => {
      el.hidden = true;
      el.textContent = '';
    });
    all('[data-loyalty-position]').forEach(el => {
      el.removeAttribute('data-cur');
      el.classList.remove('cur');
    });
    all('[data-loyalty-here]').forEach(el => { el.hidden = true; });
  }

  function invalidate() {
    generation++;
    if (pending) pending.abort();
    pending = null;
    clear();
  }

  function present(marker, rate = null) {
    const data = state(marker);
    if (!data) return;
    all('[data-loyalty-root], [data-loyalty-slot]').forEach(el => { el.hidden = data.loyaltyRetail !== 'true'; });
    if (data.loyaltyRetail !== 'true') return;
    const name = data.loyaltyName;
    if (!name) return;
    const label = `${name} member`;
    const wording = rate === null ? '' : `${rate} points per $1`;
    all('[data-loyalty-member]').forEach(el => {
      el.querySelector('[data-loyalty-name-text]').textContent = label;
      const slot = el.querySelector('[data-loyalty-rate]');
      slot.textContent = wording ? ` · ${wording}` : '';
      slot.hidden = !wording;
      el.setAttribute('aria-label', label + (wording ? ` · ${wording}` : ''));
      el.hidden = false;
    });
    all('[data-loyalty-position]').forEach(el => {
      const on = el.dataset.loyaltyPosition === name;
      el.toggleAttribute('data-cur', on);
      if (on) el.setAttribute('data-cur', '1');
      el.classList.toggle('cur', on && el.tagName === 'TD');
      const here = el.querySelector('[data-loyalty-here]');
      if (here) here.hidden = !on;
    });
  }

  function tiersFrom(body) {
    if (!object(body) || !object(body.vipProgram) || !Array.isArray(body.vipProgram.vipTiers)) return null;
    const tiers = body.vipProgram.vipTiers;
    if (!tiers.every(tier => object(tier) && text(tier.vipTierId) && text(tier.name) &&
      typeof tier.isActive === 'boolean' && Array.isArray(tier.earningRules) &&
      tier.earningRules.every(rule => object(rule) && text(rule.action) && object(rule.reward) && text(rule.reward.type)))) return null;
    return tiers;
  }

  function rateFor(tiers, name, id = '') {
    const matches = tiers.filter(tier => id ? tier.vipTierId === id : tier.name === name);
    if (matches.length !== 1 || matches[0].isActive !== true || matches[0].name !== name) return null;
    const rules = matches[0].earningRules.filter(rule => rule.action === 'purchase');
    if (rules.length !== 1 || rules[0].reward.type !== 'points-per-spend') return null;
    const rate = rules[0].reward.amount;
    return typeof rate === 'number' && Number.isFinite(rate) && rate > 0 ? rate : null;
  }

  async function request(url, options, readBody) {
    const controller = new AbortController();
    pending = controller;
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (!response.ok) return null;
      return await readBody(response);
    } catch (_) {
      return null;
    } finally {
      clearTimeout(timer);
      if (pending === controller) pending = null;
    }
  }

  async function cycle(marker) {
    restrict(marker);
    invalidate();
    current = marker;
    if (suspended || !marker?.isConnected) return;
    const data = state(marker);
    present(marker);
    if (data.loyaltyRetail !== 'true' || !stores.has(data.loyaltyStore) ||
        (!data.loyaltyName && !document.querySelector('[data-loyalty-public-rate]'))) return;
    const ticket = generation;
    const body = await request(`https://api.okendo.io/v1/stores/${data.loyaltyStore}/loyalty/vip_program`,
      { method: 'GET', credentials: 'omit', cache: 'no-store' }, response => response.json());
    if (suspended || ticket !== generation || current !== marker || !marker.isConnected) return;
    const tiers = tiersFrom(body);
    if (!tiers) return;
    present(marker, rateFor(tiers, data.loyaltyName, data.loyaltyId));
    all('[data-loyalty-public-rate]').forEach(el => {
      const rate = rateFor(tiers, el.dataset.loyaltyPublicRate);
      if (rate !== null) {
        el.textContent = `${rate} points per $1`;
        el.hidden = false;
      }
    });
  }

  async function revalidate() {
    invalidate();
    suspended = true;
    const ticket = generation;
    const anchor = current?.isConnected ? current : document.querySelector(contextSelector);
    if (!anchor) return;
    const url = new URL(anchor.dataset.loyaltyRefresh, location.origin);
    if (url.origin !== location.origin) return;
    url.searchParams.set('section_id', 'cart-drawer');
    const html = await request(url.href, { method: 'GET', credentials: 'same-origin', cache: 'no-store' }, response => response.text());
    if (ticket !== generation || !anchor.isConnected || document.hidden || !html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const markers = parsed.querySelectorAll('cart-drawer-items [data-loyalty-context="drawer"]');
    if (markers.length !== 1 || !stores.has(markers[0].dataset.loyaltyStore) ||
        markers[0].dataset.loyaltyStore !== anchor.dataset.loyaltyStore) return;
    // Adopt only our passive server marker. Never replay scripts or replace cart code.
    const fresh = document.importNode(markers[0], true);
    anchor.replaceWith(fresh);
    suspended = false;
    cycle(fresh);
  }

  const observer = new MutationObserver(records => {
    let latest = null;
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const markers = node.matches(contextSelector) ? [node] : node.querySelectorAll(contextSelector);
        for (const marker of markers) if (marker.isConnected) latest = marker;
      }
    }
    if (latest && latest !== current) {
      restrict(latest);
      const changedAccount = current && state(current).loyaltyAccount !== state(latest).loyaltyAccount;
      if (suspended) {
        // A late cart response must not undo pagehide/logout clearing. Only an
        // explicit history/visibility return may resume personalization.
        current = latest;
        invalidate();
      } else if (changedAccount) {
        current = latest;
        revalidate();
      } else cycle(latest);
    } else if (current && !current.isConnected) invalidate();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  function suspend() {
    suspended = true;
    invalidate();
  }
  window.addEventListener('pagehide', suspend);
  window.addEventListener('pageshow', event => { if (event.persisted) revalidate(); });
  window.addEventListener('popstate', revalidate);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) suspend();
    else revalidate();
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin === location.origin && /\/(?:account|customer_authentication)(?:\/|$)/.test(url.pathname)) suspend();
  }, true);

  current = document.querySelector('cart-drawer-items [data-loyalty-context]') || document.querySelector(contextSelector);
  restrict(current);
  if (document.hidden) suspend();
  else if (current) cycle(current);
})();
